import { useQuery } from '@tanstack/react-query';
import { registerOrganizationQueries } from '@repo/api-kit/queries/register-organization';
import { registerPartnersQueries } from '@repo/api-kit/queries/register-partners';
import { useCurrentOrganizationId } from '@repo/runtime-kit/organizations/hooks/use-current-organization-id';

// Mirrors register-partners-js's own useIsCobrandedDougs, which matches the
// partner registration's partner name — kept as a separate copy here rather
// than a cross-kit/cross-app import (company-creation-kit has its own
// independent implementation too).
//
// Resolves the partner name via organization.partnerRegistrationId rather
// than the firstparty onboarding_partner endpoint (requires a fully
// authenticated session, unavailable pre-signup) or
// registration_product_selections (only exists once a price plan is
// selected, after this screen). partnerRegistrationId is set from the very
// start of registration and needs no special auth, so this works at every
// stage. Shared within this kit by both the nature-of-ops screen and the
// checkout screens.
const DOUGS_PARTNER_IDENTIFIERS = [
  'dougs-dougs-comptepro-526',
  'dougs - compte pro',
  'dougs - dougs pro account staging',
  'dougs - dougs - compte pro (embed)',
];
export const DOUGS_PARTNER_DISPLAY_NAME = 'Dougs';

export function isDougsPartnerName(partnerName?: string): boolean {
  return Boolean(partnerName && DOUGS_PARTNER_IDENTIFIERS.includes(partnerName.toLowerCase()));
}

export function useIsCobrandedDougs(): boolean {
  const organizationId = useCurrentOrganizationId();
  const { data: organization } = useQuery({
    ...registerOrganizationQueries.item(organizationId),
    enabled: Boolean(organizationId),
  });

  const partnerRegistrationId = organization?.partnerRegistrationId;
  const { data: partnerRegistrationResponse } = useQuery({
    ...registerPartnersQueries.partnerRegistration(partnerRegistrationId ?? ''),
    enabled: Boolean(partnerRegistrationId),
  });

  return isDougsPartnerName(partnerRegistrationResponse?.partnerRegistration?.partnerName);
}

export function useDougsPartnerName(): string | undefined {
  return useIsCobrandedDougs() ? DOUGS_PARTNER_DISPLAY_NAME : undefined;
}
